// CapturedPanel — horizontal strip of the pieces THIS player has
// captured from the opponent. Sits next to each player's profile
// card (opponent's above the board, yours below), so the "left /
// right" wording in the original spec reduces to "tray next to the
// corresponding SeatCard" — matches every major chess UI.
//
// Material-score pip: instead of a separate chip, if this side is
// ahead on material we render a "+N" tag at the END of the captured
// strip (per user spec). Hidden when the side is equal or behind.
//
// We compute captures by diffing the live FEN against the starting
// piece counts — same approach the server uses in
// `engine.capturedSummary()`. Pure-function, no props-drilled state.

import { useMemo } from 'react';
import { parseFen, materialPoints } from './rules.js';
import type { PieceCode } from './Board';

type Props = {
  fen: string;
  /** Which side this panel represents. Shows the pieces this side
   *  has captured (i.e., the OPPONENT's missing pieces). */
  side: 'w' | 'b';
};

// Starting piece counts by code. Used to diff against current board
// counts → (starting - current) is the number of that piece captured.
const STARTING_COUNTS: Record<PieceCode, number> = {
  wP: 8, wN: 2, wB: 2, wR: 2, wQ: 1, wK: 1,
  bP: 8, bN: 2, bB: 2, bR: 2, bQ: 1, bK: 1,
};

// Display order within a tray — pawns first, then minors, then
// heavies. Matches chess.com's captured-pieces order.
const DISPLAY_ORDER = ['P', 'N', 'B', 'R', 'Q'] as const;

export default function CapturedPanel({ fen, side }: Props) {
  const { tray, diff } = useMemo(() => {
    try {
      const pos = parseFen(fen);
      const liveCounts: Record<string, number> = {};
      for (const p of pos.board) if (p) liveCounts[p] = (liveCounts[p] || 0) + 1;

      // The pieces THIS side has captured are the OPPONENT's missing
      // pieces. For example, if `side = 'w'`, we show missing `b*`
      // pieces (white captured them from black).
      const enemyColour = side === 'w' ? 'b' : 'w';
      const captured: PieceCode[] = [];
      for (const type of DISPLAY_ORDER) {
        const code = (enemyColour + type) as PieceCode;
        const missing = (STARTING_COUNTS[code] || 0) - (liveCounts[code] || 0);
        for (let i = 0; i < Math.max(0, missing); i++) captured.push(code);
      }
      const mine      = materialPoints(pos, side);
      const theirs    = materialPoints(pos, enemyColour);
      const materialDiff = mine - theirs;
      return { tray: captured, diff: materialDiff };
    } catch {
      return { tray: [] as PieceCode[], diff: 0 };
    }
  }, [fen, side]);

  return (
    <div
      className={[
        'chess-captured',
        tray.length === 0 && diff <= 0 ? 'chess-captured--empty' : '',
      ].join(' ')}
      aria-label={`${side === 'w' ? 'White' : 'Black'} captured pieces`}
    >
      {tray.map((code, i) => (
        <img
          key={`${code}-${i}`}
          src={`/games/chess/pieces/${code}.png`}
          alt={code}
          className="chess-captured__piece"
          draggable={false}
        />
      ))}
      {diff > 0 && (
        <span className="chess-captured__pip" title={`Material lead: ${diff}`}>
          +{diff}
        </span>
      )}
    </div>
  );
}
